import React, { useEffect } from 'react';
import Paper, { PaperHeader } from '../common/layout/Paper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import Button from '../common/buttons/Button';
import { styled } from 'styled-components';
import { SubHeader } from '../common/layout/PageHeader';
import { useNavigate } from 'react-router-dom';
import { Grid } from '../common/layout/Content';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme, useMediaQuery } from '@mui/material';
import { getQuickLinkIconFromUrl } from '../../utils/company-utils';
import { fetchCompany } from '../../data/thunks/companyThunk';
import { Spinner } from '../common/BusySpinner';

interface ContainerProps {
	isMobile?: boolean;
}

interface LinksProps {
	links: any[];
	isMobile: boolean;
}

const QuickLinks = () => {
	const dispatch = useDispatch<AppDispatch>();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const {
		company: { quickLinks, initialized },
	} = useSelector((state: RootState) => state.companyState);

	useEffect(() => {
		dispatch(fetchCompany());
	}, []);

	return (
		<Paper gradientBorder>
			<ContentContainer>
				<PaperHeader>Quick Links</PaperHeader>
				<QuickLinksContainer>
					{initialized ? (
						<Links links={quickLinks} isMobile={isMobile} />
					) : (
						<BusySpinnerContainer>
							<Spinner />
						</BusySpinnerContainer>
					)}
				</QuickLinksContainer>
			</ContentContainer>
		</Paper>
	);
};

const ContentContainer = styled.div`
	min-height: 200px;
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const QuickLinksContainer = styled.div`
	padding: 20px;
	flex: 1;
	display: flex;
	flex-direction: column;
`;

const BusySpinnerContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
`;

export default QuickLinks;

const Links = ({ links, isMobile }: LinksProps) => {
	if (!links.length) {
		return <NotInitialized />;
	}

	return (
		<Grid container spacing={isMobile ? 1 : 3}>
			{links.map((link) => (
				<Grid item xs={12} sm={6} md={6} key={link.id}>
					<Paper border>
						<Container isMobile={isMobile}>
							<ItemContainer>
								{getIcon(link.url)}
								<div>{link.title}</div>
							</ItemContainer>
							<ItemContainer>
								<Button
									onClick={() =>
										window.open(link.url, '_blank')
									}
									variant={'text'}
									sz={'small'}
									startIcon={<OpenInNewIcon />}
								>
									OPEN
								</Button>
							</ItemContainer>
						</Container>
					</Paper>
				</Grid>
			))}
		</Grid>
	);
};

const Container = styled.div<ContainerProps>`
	display: flex;
	gap: 10px;
	flex-direction: column;
	justify-content: center;
	align-items: left;
	padding-top: 10px;
	padding-bottom: 10px;
	padding-left: ${(props) => (props.isMobile ? '5px' : '10px')};
	padding-right: ${(props) => (props.isMobile ? '5px' : '10px')};
`;

const ItemContainer = styled.div`
	display: flex;
	gap: 8px;
	justify-content: left;
	align-items: left;
`;

const getIcon = (url) => {
	const quickLinkIcon = getQuickLinkIconFromUrl(url);
	return (
		<img
			style={{ height: 20, objectFit: 'cover' }}
			src={quickLinkIcon.icon}
			alt={quickLinkIcon.alt}
		/>
	);
};

const NotInitialized = () => {
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<NotInitializedContainer>
			<SubHeader
				style={{
					fontSize: isMobile ? '1rem' : '1.25rem',
					textAlign: 'center',
				}}
			>
				Quick Links have not been set up
			</SubHeader>
			<Button onClick={() => navigate('/company')}>
				ADD QUICK LINKS
			</Button>
		</NotInitializedContainer>
	);
};

const NotInitializedContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding-bottom: 40px;
`;
